// Client-side invoice/receipt document generator. Renders a print-ready A4
// HTML invoice in a new window and triggers the browser print dialog, so the
// user can "Save as PDF". Zero server dependency (no pdfkit/puppeteer) — works
// identically in dev (node) and prod (k8s).
//
// Issuer details are placeholders; wire real SAW legal entity (name, address,
// NPWP) via env/config once finalized.
import { formatIDR } from './billing.format'

const ISSUER = {
  name: 'Everest EBC',
  legal: 'PT Solusi Adikarya Wisesa',
  tagline: 'Employee Background Check',
}

function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

function statusLabel(status) {
  const map = { paid: 'PAID', unpaid: 'UNPAID', canceled: 'CANCELED', expired: 'EXPIRED' }
  return map[status] || String(status || '').toUpperCase()
}

function companyName(company) {
  if (!company) return '—'
  return (
    company.name ||
    company.legal_name ||
    company.company_information?.name ||
    company.company_information?.legal_name ||
    '—'
  )
}

function companyRef(company) {
  if (!company) return ''
  const cid = company.company_id || company.id
  if (!cid) return ''
  const s = String(cid).trim()
  return /^id[-\s]/i.test(s) ? s : `ID-${s}`
}

// Line items differ by invoice_type. Returns [{ desc, qty, unit, amount }].
function lineItems(inv) {
  const subtotal = Number(inv.subtotal_idr || 0)
  if (inv.invoice_type === 'subscription') {
    const months = inv.pricing_plan?.billing_cycle === 'annual'
      ? 12
      : inv.pricing_plan?.billing_cycle === '6_months'
        ? 6
        : 1
    const planName = inv.pricing_plan?.name || 'Subscription'
    const period =
      inv.period_start && inv.period_end
        ? ` (${formatDate(inv.period_start)} – ${formatDate(inv.period_end)})`
        : ''
    return [
      {
        desc: `Platform fee — ${planName}${period}`,
        qty: months,
        unit: months > 1 ? Number(inv.platform_fee_idr || subtotal) / months : null,
        amount: subtotal,
      },
    ]
  }
  if (inv.invoice_type === 'topup') {
    return [
      {
        desc: `Top up enquiry quota${inv.pricing_plan?.name ? ` — ${inv.pricing_plan.name}` : ''}`,
        qty: Number(inv.quota_amount || 0),
        unit: Number(inv.per_enquiry_fee_idr || 0),
        amount: subtotal,
      },
    ]
  }
  return [
    {
      desc: `Quota purchase${inv.quota_amount ? ` — ${inv.quota_amount} enquiry` : ''}`,
      qty: Number(inv.quota_amount || 1),
      unit: null,
      amount: subtotal,
    },
  ]
}

const DOC_CSS = `
  * { box-sizing: border-box; }
  body { font-family: 'Inter', -apple-system, Segoe UI, Roboto, sans-serif; color: #181d27; margin: 0; padding: 32px; }
  .doc { max-width: 720px; margin: 0 auto; }
  .row { display: flex; justify-content: space-between; align-items: flex-start; }
  .brand { font-size: 20px; font-weight: 700; color: #14378e; }
  .brand small { display: block; font-size: 11px; font-weight: 500; color: #535862; }
  .title { font-size: 26px; font-weight: 700; letter-spacing: 1px; }
  .muted { color: #535862; font-size: 12px; }
  .badge { display: inline-block; padding: 3px 10px; border-radius: 6px; font-size: 12px; font-weight: 600; border: 1px solid; }
  .badge.paid { background: #ecfdf3; border-color: #abefc6; color: #067647; }
  .badge.unpaid { background: #fffaeb; border-color: #fedf89; color: #b54708; }
  .badge.canceled, .badge.expired { background: #fef3f2; border-color: #fecdca; color: #b42318; }
  .grid { display: flex; gap: 48px; margin: 24px 0; }
  .grid h4 { margin: 0 0 6px; font-size: 11px; text-transform: uppercase; letter-spacing: .5px; color: #717680; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th { text-align: left; font-size: 11px; text-transform: uppercase; color: #717680; border-bottom: 1px solid #e9eaeb; padding: 8px 0; }
  th.r, td.r { text-align: right; }
  td { padding: 12px 0; border-bottom: 1px solid #f2f4f7; font-size: 13px; }
  .totals { margin-top: 12px; margin-left: auto; width: 280px; }
  .totals .line { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; color: #535862; }
  .totals .grand { border-top: 1px solid #e9eaeb; margin-top: 6px; padding-top: 10px; font-weight: 700; font-size: 15px; color: #181d27; }
  .foot { margin-top: 40px; font-size: 11px; color: #717680; border-top: 1px solid #e9eaeb; padding-top: 16px; }
  @media print { body { padding: 0; } .doc { max-width: none; } @page { margin: 18mm; } }
`

function invoiceBody(inv, company) {
  const items = lineItems(inv)
  const appFee = Number(inv.app_fee_idr || 0)
  const tax = Number(inv.tax_amount_idr || 0)
  const pd = inv.payment_detail || {}
  const isReceipt = inv.status === 'paid'
  return `
    <div class="row">
      <div>
        <div class="brand">${ISSUER.name}<small>${ISSUER.legal} · ${ISSUER.tagline}</small></div>
      </div>
      <div style="text-align:right">
        <div class="title">${isReceipt ? 'RECEIPT' : 'INVOICE'}</div>
        <div class="muted">${inv.invoice_number || ''}</div>
        <div style="margin-top:6px"><span class="badge ${inv.status}">${statusLabel(inv.status)}</span></div>
      </div>
    </div>

    <div class="grid">
      <div>
        <h4>Bill to</h4>
        <div style="font-weight:600">${companyName(company)}</div>
        <div class="muted">${companyRef(company)}</div>
      </div>
      <div>
        <h4>Issued</h4>
        <div>${formatDate(inv.created_at)}</div>
      </div>
      <div>
        <h4>${isReceipt ? 'Paid on' : 'Due date'}</h4>
        <div>${isReceipt ? formatDate(pd.paid_at || inv.paid_at) : formatDate(inv.due_date)}</div>
      </div>
    </div>

    <table>
      <thead>
        <tr><th>Description</th><th class="r">Qty</th><th class="r">Unit</th><th class="r">Amount</th></tr>
      </thead>
      <tbody>
        ${items
          .map(
            (it) => `<tr>
              <td>${it.desc}</td>
              <td class="r">${it.qty ? it.qty.toLocaleString('id-ID') : '—'}</td>
              <td class="r">${it.unit != null ? formatIDR(it.unit) : '—'}</td>
              <td class="r">${formatIDR(it.amount)}</td>
            </tr>`,
          )
          .join('')}
      </tbody>
    </table>

    <div class="totals">
      <div class="line"><span>Subtotal</span><span>${formatIDR(inv.subtotal_idr)}</span></div>
      <div class="line"><span>App fee</span><span>${formatIDR(appFee)}</span></div>
      <div class="line"><span>Tax</span><span>${formatIDR(tax)}</span></div>
      <div class="line grand"><span>Total</span><span>${formatIDR(inv.total_idr)}</span></div>
    </div>

    ${
      pd.va_number && !isReceipt
        ? `<div class="foot">Payment — ${pd.va_bank || ''} Virtual Account: <strong>${pd.va_number}</strong></div>`
        : ''
    }
    <div class="foot">
      ${isReceipt ? 'Thank you for your payment.' : 'Please complete payment before the due date.'}
      This document was generated by ${ISSUER.name}.
    </div>
  `
}

function openPrintWindow(title, inner) {
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>${title}</title><style>${DOC_CSS}</style></head><body><div class="doc">${inner}</div><script>window.onload=function(){setTimeout(function(){window.print()},250)}<\/script></body></html>`
  const w = window.open('', '_blank', 'width=820,height=1000')
  if (!w) {
    // Popup blocked — fall back to a Blob download of the HTML.
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${title}.html`
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 2000)
    return
  }
  w.document.open()
  w.document.write(html)
  w.document.close()
}

// Per-invoice download (opens print → Save as PDF).
export function downloadInvoiceDocument(invoice, company) {
  if (!invoice) return
  openPrintWindow(invoice.invoice_number || 'invoice', invoiceBody(invoice, company))
}

// "Download all" — a single statement listing every invoice.
export function downloadInvoiceStatement(invoices, company) {
  const list = Array.isArray(invoices) ? invoices : []
  const rows = list
    .map(
      (inv) => `<tr>
        <td>${inv.invoice_number || ''}<div class="muted">${formatDate(inv.created_at)}</div></td>
        <td>${
          inv.invoice_type === 'subscription'
            ? 'Platform fee'
            : inv.invoice_type === 'topup'
              ? `Top up ${inv.quota_amount} enquiry`
              : 'Quota purchase'
        }</td>
        <td class="r">${formatIDR(inv.total_idr)}</td>
        <td class="r"><span class="badge ${inv.status}">${statusLabel(inv.status)}</span></td>
      </tr>`,
    )
    .join('')
  const inner = `
    <div class="row">
      <div class="brand">${ISSUER.name}<small>${ISSUER.legal} · ${ISSUER.tagline}</small></div>
      <div style="text-align:right"><div class="title">STATEMENT</div><div class="muted">${formatDate(new Date().toISOString())}</div></div>
    </div>
    <div class="grid"><div><h4>Company</h4><div style="font-weight:600">${companyName(company)}</div><div class="muted">${companyRef(company)}</div></div></div>
    <table>
      <thead><tr><th>Invoice</th><th>Description</th><th class="r">Total</th><th class="r">Status</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="4" class="muted">No invoices.</td></tr>'}</tbody>
    </table>
    <div class="foot">Generated by ${ISSUER.name}.</div>
  `
  openPrintWindow(`statement-${companyRef(company) || 'company'}`, inner)
}
